import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import Button from '@/components/ui/Button'
import { Field, Input, PasswordInput } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'
import AuthShell from './AuthShell'

/**
 * Reached from the emailed link, which carries the token and email as query
 * parameters. Also completes a staff invite: an admin creates the account and
 * the operator sets their own password here, so nobody else ever knows it.
 */
export default function ResetPassword() {
  const toast = useToast()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [form, setForm] = useState({
    email: params.get('email') ?? '',
    password: '',
    password_confirmation: '',
  })
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})
    try {
      const { data } = await api.post('/auth/reset-password', { ...form, token })
      toast.success('Password updated', data.message)
      navigate('/login')
    } catch (err) {
      setErrors(fieldErrors(err))
      toast.error('Could not update the password', errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <AuthShell
        heading="Link incomplete"
        subheading="This page needs the link from your email"
        tagline={<>Open the link we emailed you.</>}
      >
        <div className="mt-7 space-y-4">
          <div className="rounded-lg border border-line bg-foreground/5 p-4 text-sm text-foreground/80">
            Open the reset link straight from your email so it carries its token, or request a new one.
          </div>
          <Link to="/forgot-password">
            <Button variant="outline" className="w-full">
              Request a new link
            </Button>
          </Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      heading="Choose a password"
      subheading="Pick something you have not used elsewhere"
      tagline={
        <>
          One password away <br /> from getting started.
        </>
      }
    >
      <form onSubmit={submit} className="mt-7 space-y-4">
        <Field error={errors.email?.[0]} htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </Field>
        <Field error={errors.password?.[0]} htmlFor="password">
          <PasswordInput
            id="password"
            autoComplete="new-password"
            placeholder="New password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
        </Field>
        <Field htmlFor="password_confirmation">
          <PasswordInput
            id="password_confirmation"
            autoComplete="new-password"
            placeholder="Confirm new password"
            value={form.password_confirmation}
            onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
            required
          />
        </Field>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button type="submit" loading={loading}>
            Save password
          </Button>
          <Link to="/login">
            <Button variant="outline" className="w-full">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </AuthShell>
  )
}
